/**
 * Exam block (PLAN-Z M6, PLAN.md §3): the mode that mirrors the real test —
 * one subtest, 20 items, one 25:00 countdown for the whole block, free
 * navigation with revisable answers, auto-submit at zero. No pause button,
 * no scratchpad, no calculator. Explanations are withheld until the block
 * ends, then shown per item on the Results screen.
 */
import { useEffect, useRef, useState } from 'react'
import {
  BLOCK_ITEMS,
  BLOCK_MINUTES,
  buildBlock,
  gradeBlockItem,
  paceAheadMs,
  type BlockItem,
  type ItemAnswer,
} from './block.ts'
import { Results, type BlockResultData } from './Results.tsx'
import { newId, saveAttempt, type Attempt } from '../data/local.ts'
import { pushAttempts } from '../data/sync.ts'
import { QuestionCard } from '../render/QuestionCard.tsx'
import { EquationsInputs, EquationsSystem } from '../render/Equations.tsx'
import { LatinAnswer, LatinGrid } from '../render/LatinGrid.tsx'
import { FiguresOptions } from '../render/Figures.tsx'
import type { EquationsPrompt } from '../generators/equations.ts'
import type { FiguresPrompt } from '../generators/figures.ts'
import type { LatinPrompt } from '../generators/latin.ts'
import type { Subtest } from '../generators/types.ts'

function fmtClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** Dev/testing affordance: ?minutes= shortens the countdown (default 25). */
function readMinutes(): number {
  const raw = Number(new URLSearchParams(window.location.search).get('minutes') ?? BLOCK_MINUTES)
  if (!Number.isFinite(raw) || raw <= 0) return BLOCK_MINUTES
  return Math.min(BLOCK_MINUTES, raw)
}

export function ExamBlockPage() {
  const [phase, setPhase] = useState<'setup' | 'running' | 'results'>('setup')
  const [subtest, setSubtest] = useState<Subtest>('equations')
  const [blockSeed, setBlockSeed] = useState(() => Math.floor(Math.random() * 1e9))
  const [items, setItems] = useState<BlockItem[] | null>(null)
  const [answers, setAnswers] = useState<ItemAnswer[]>([])
  const [durations, setDurations] = useState<number[]>([])
  const [index, setIndex] = useState(0)
  const [remaining, setRemaining] = useState(BLOCK_MINUTES * 60_000)
  const [result, setResult] = useState<BlockResultData | null>(null)
  const [minutes, setMinutes] = useState(BLOCK_MINUTES)

  const enteredAtRef = useRef(0)
  const deadlineRef = useRef(0)
  const submittedRef = useRef(false)
  const blockIdRef = useRef('')
  const liveRef = useRef({ answers, durations, index, items })
  liveRef.current = { answers, durations, index, items }

  const startBlock = (seed: number, sub: Subtest) => {
    const mins = readMinutes()
    setMinutes(mins)
    setBlockSeed(seed)
    setSubtest(sub)
    setItems(buildBlock(sub, seed))
    setAnswers(Array(BLOCK_ITEMS).fill(null))
    setDurations(Array(BLOCK_ITEMS).fill(0))
    setIndex(0)
    setRemaining(Math.round(mins * 60_000))
    deadlineRef.current = Date.now() + Math.round(mins * 60_000)
    enteredAtRef.current = performance.now()
    submittedRef.current = false
    blockIdRef.current = newId()
    setResult(null)
    setPhase('running')
  }

  const submitBlock = (auto: boolean) => {
    if (submittedRef.current) return
    submittedRef.current = true
    const { answers: ans, durations: dur, index: idx, items: block } = liveRef.current
    const finalDurations = [...dur]
    finalDurations[idx] += Math.round(performance.now() - enteredAtRef.current)
    const totalElapsed = Math.round(minutes * 60_000) - Math.max(0, deadlineRef.current - Date.now())

    const attempts: Attempt[] = (block ?? []).flatMap((item) =>
      gradeBlockItem(item, ans[item.index] ?? null).map((graded) => ({
        id: newId(),
        sessionId: blockIdRef.current,
        itemId: `${blockIdRef.current}-item-${item.index}`,
        subtest: item.question.subtest,
        difficulty: item.difficulty,
        seed: item.seed,
        correct: graded.correct,
        subAnswers: graded.subAnswers,
        durationMs: finalDurations[item.index],
        answeredAt: new Date().toISOString(),
      })),
    )
    void Promise.all(attempts.map(saveAttempt))
    void pushAttempts(attempts)

    setResult({
      subtest,
      blockSeed,
      items: block ?? [],
      answers: ans,
      durations: finalDurations,
      attempts,
      autoSubmitted: auto,
      elapsedMs: totalElapsed,
    })
    setPhase('results')
  }

  // Countdown + auto-submit at zero. Interval only while running.
  useEffect(() => {
    if (phase !== 'running') return
    const timer = window.setInterval(() => {
      const rem = deadlineRef.current - Date.now()
      setRemaining(Math.max(0, rem))
      if (rem <= 0) submitBlock(true)
    }, 250)
    return () => window.clearInterval(timer)
    // submitBlock is stable enough via refs; it never leaves stale state behind
    // because all live values are read through liveRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const navigate = (i: number) => {
    if (submittedRef.current || i === index) return
    const finalDurations = [...durations]
    finalDurations[index] += Math.round(performance.now() - enteredAtRef.current)
    setDurations(finalDurations)
    setIndex(i)
    enteredAtRef.current = performance.now()
  }

  const setAnswer = (value: ItemAnswer) => {
    setAnswers((prev) => {
      const copy = [...prev]
      copy[index] = value
      return copy
    })
  }

  if (phase === 'setup') {
    return (
      <div className="exam-page">
        <section className="question-card">
          <h2 className="question-card-title">Exam block</h2>
          <p>
            One subtest, <strong>20 items</strong>, a single <strong>25:00</strong> countdown,
            free navigation, revisable answers, auto-submit at zero. No pause, no scratchpad, no
            calculator — like the real thing.
          </p>
          <div className="exam-setup-row" role="group" aria-label="Choose subtest">
            {(['equations', 'latin', 'figures'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={s === subtest ? 'exam-subtest selected' : 'exam-subtest'}
                aria-pressed={s === subtest}
                onClick={() => setSubtest(s)}
              >
                {s === 'equations'
                  ? 'Mathematical Equations'
                  : s === 'latin'
                    ? 'Latin Squares'
                    : 'Figure Sequences'}
              </button>
            ))}
          </div>
          <p className="muted">Block seed {blockSeed} — retake the same block with the same seed.</p>
          <button type="button" className="question-submit" onClick={() => startBlock(blockSeed, subtest)}>
            Start block
          </button>
        </section>
      </div>
    )
  }

  if (phase === 'results' && result) {
    return (
      <Results
        data={result}
        onRetake={() => startBlock(result.blockSeed, result.subtest)}
        onNewBlock={() => {
          setBlockSeed(Math.floor(Math.random() * 1e9))
          setPhase('setup')
        }}
      />
    )
  }

  // running
  const item = items![index]
  const answered = (i: number): boolean => {
    const a = answers[i]
    if (a === null || a === undefined) return false
    if (typeof a === 'string') return a !== ''
    return Object.values(a).some((v) => v !== null && v !== undefined && v !== '')
  }
  const unanswered = items!.length - items!.filter((_, i) => answered(i)).length
  const ahead = paceAheadMs(minutes * 60_000 - remaining, index)
  const paceText =
    ahead >= 0
      ? `item ${index + 1} of ${BLOCK_ITEMS} · ${fmtClock(ahead)} ahead of pace`
      : `item ${index + 1} of ${BLOCK_ITEMS} · ${fmtClock(-ahead)} behind pace`

  const eqPrompt: EquationsPrompt | null =
    item.question.subtest === 'equations' ? (item.question.prompt as EquationsPrompt) : null
  const latinPrompt: LatinPrompt | null =
    item.question.subtest === 'latin' ? (item.question.prompt as LatinPrompt) : null

  return (
    <div className="exam-page">
      <div className="exam-header">
        <span className={`exam-countdown${remaining < 60_000 ? ' low' : ''}`} aria-label="Countdown">
          {fmtClock(remaining)}
        </span>
        <span className="exam-pace">{paceText}</span>
        <button type="button" className="drill-exit" onClick={() => submitBlock(false)}>
          Submit block
        </button>
      </div>
      <div className="exam-nav" role="group" aria-label="Item navigation">
        {items!.map((_, i) => (
          <button
            key={i}
            type="button"
            className={
              'exam-nav-item' +
              (i === index ? ' current' : '') +
              (answered(i) ? ' answered' : '')
            }
            aria-label={`Go to item ${i + 1}${answered(i) ? ' (answered)' : ''}`}
            aria-current={i === index}
            onClick={() => navigate(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <p className="muted exam-unanswered">{unanswered} unanswered</p>
      <QuestionCard
        title={`Item ${index + 1} of ${BLOCK_ITEMS} · ${item.difficulty}`}
        prompt={
          eqPrompt ? (
            <EquationsSystem prompt={eqPrompt} />
          ) : latinPrompt ? (
            <LatinGrid prompt={latinPrompt} />
          ) : null
        }
        answer={
          eqPrompt ? (
            <EquationsInputs
              prompt={eqPrompt}
              values={(answers[index] as Record<string, string>) ?? {}}
              onChange={(letter, value) =>
                setAnswer({
                  ...((answers[index] as Record<string, string>) ?? {}),
                  [letter]: value,
                })
              }
            />
          ) : item.question.subtest === 'figures' ? (
            <FiguresOptions
              options={(item.question.prompt as FiguresPrompt).options}
              picks={
                answers[index] && typeof answers[index] === 'object'
                  ? [
                      (answers[index] as { blank1: number | null }).blank1 ?? null,
                      (answers[index] as { blank2: number | null }).blank2 ?? null,
                    ]
                  : [null, null]
              }
              onPick={(blank, optionIndex) =>
                setAnswer({
                  ...((answers[index] as { blank1: number | null; blank2: number | null }) ?? {
                    blank1: null,
                    blank2: null,
                  }),
                  [blank === 0 ? 'blank1' : 'blank2']: optionIndex,
                })
              }
            />
          ) : (
            <LatinAnswer
              selected={typeof answers[index] === 'string' ? (answers[index] as string) : null}
              onSelect={(letter) => setAnswer(letter)}
            />
          )
        }
        onSubmit={() => submitBlock(false)}
        submitLabel="Submit block"
        feedback={undefined}
      />
    </div>
  )
}
